import React, { useEffect, useState } from "react";
import { Smartphone, X, Share2, Plus } from "lucide-react";

const DISMISS_KEY = "wpf-install-dismissed-v1";
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isMobile = () =>
  /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    if (!isMobile()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    // Android / Chrome: capture the native beforeinstallprompt for a real install button
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS: no beforeinstallprompt — show a manual "Add to Home Screen" guide after a short delay
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
        localStorage.setItem(DISMISS_KEY, "1");
      }
    } else if (isIOS()) {
      setShowIosGuide(true);
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="install-banner"
        className="fixed inset-x-3 bottom-3 z-[60] border-2 border-ink bg-white shadow-hard md:inset-x-auto md:right-4 md:max-w-md"
      >
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-ink bg-ink text-bone">
            <Smartphone size={20} />
          </div>
          <div className="flex-1">
            <div className="font-alfa text-base uppercase leading-tight">Save to your phone</div>
            <p className="mt-1 font-serif text-sm leading-snug text-ink/80">
              Add When Pigs Fly BBQ to your home screen — re-order in one tap.
            </p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="install-banner-dismiss"
            className="border border-ink p-1.5 text-ink transition-colors hover:bg-ink hover:text-bone"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex border-t border-ink">
          <button
            onClick={install}
            data-testid="install-banner-install"
            className="flex-1 bg-ink py-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone transition-colors hover:bg-bone hover:text-ink"
          >
            {isIOS() ? "Show how" : "Add to home screen"}
          </button>
        </div>
      </div>

      {showIosGuide && (
        <div
          className="fixed inset-0 z-[70] flex items-end bg-ink/60 backdrop-blur-sm md:items-center md:justify-center"
          onClick={() => setShowIosGuide(false)}
        >
          <div
            className="w-full border-2 border-ink bg-white p-6 md:max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="install-ios-guide"
          >
            <div className="flex items-center justify-between">
              <div className="eyebrow text-ink/70">iPhone · Safari</div>
              <button aria-label="Close" onClick={() => setShowIosGuide(false)} className="border border-ink p-1.5 text-ink hover:bg-ink hover:text-bone">
                <X size={14} />
              </button>
            </div>
            <h3 className="mt-3 font-alfa text-2xl uppercase leading-tight">Add to Home Screen</h3>
            <ol className="mt-4 space-y-3 font-serif text-sm text-ink/85">
              <li className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center border border-ink font-mono text-xs">1</span>
                Tap the <Share2 size={16} className="inline" aria-label="share" /> share button in Safari.
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center border border-ink font-mono text-xs">2</span>
                Scroll and tap <strong>Add to Home Screen</strong> <Plus size={16} className="inline" />.
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center border border-ink font-mono text-xs">3</span>
                Tap <strong>Add</strong> in the top right.
              </li>
            </ol>
            <button
              onClick={() => { setShowIosGuide(false); dismiss(); }}
              className="mt-6 w-full border-2 border-ink bg-ink py-3 font-mono text-xs uppercase tracking-[0.24em] text-bone hover:bg-bone hover:text-ink"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
